import React, {Dispatch, useCallback, useEffect, useState} from "react";
import {
    AtRiskReason,
    AtRiskReasonValueToName,
    AuthorizationBillItem,
    AuthorizationInfo,
    StaffingFormInput,
    OfferedHoursStatus,
    OfferedHoursStatusMap,
} from "../../domain";
import {SortBy} from "common/component/EnrichedTable";
import {SubmittedRequestType as RequestType, SubmittedRequestInfo} from "../../submitted-request/SubmittedRequestDomain";
import {
    reduceRelayPaginationAction,
    RelayPagination,
    RelayPaginationActionPayload,
    RelayPaginationState,
    useRelayPagination
} from "providerapp/pagination";
import {SortOrder} from "../../../../placement/domains";
import {AuthorizationBillingTable} from "./AuthorizationBillingTable";
import {gql} from "@apollo/client";
import {RelayConnection, showToast, ErrorDisplay} from "common";
import {useLazyQuery$} from "graphqlsupport";
import {useObservable} from "rxsupport";
import {Pane} from "evergreen-ui";
import {FatOutlineButton} from "../../../../common/component/FatOutlineButton";
import {useSubmitAtRiskReasonsMutation$} from "./useSubmitAtRiskReasonsMutation$";
import {StaffingPlanFormModal} from "../../staffing-plan/StaffingPlanFormModal";
import {BehaviorSubject} from "rxjs";
import {TreatmentHoldFormModal} from "../../offer-at-risk/family-init-hold/TreatmentHoldFormModal";
import AuthorizationBillingTabFilter from "./AuthorizationBillingTabFilter";
import {StaffingPlanActionModal} from "../../staffing-plan/StaffingPlanActionModal";
import {
    StaffingAction,
    StaffingAddendumAction,
    StaffingAddendumFinisher,
    StaffingAddendumType,
    useStaffingAction
} from "../../staffing-plan/domain";
import {ClientReportModal} from "../../ClientReportModal";
import _ from "lodash";
import {
    buildNotAnActiveClientDescription,
    ConsultWithCCMNotAnActiveClientFormModal
} from "../../offer-at-risk/not-active-client/ConsultWithCCMNotAnActiveClientFormModal";
import {ClientTransferringFormModal} from "../../offer-at-risk/client-transferring/ClientTransferringFormModal";
import {NoContactFormModal} from "../../offer-at-risk/no-contact/NoContactFormModal";
import {getErrMsgForSelectingActiveClient, isSelectingBilledMonths} from "../../domain/offer-at-risk";
import {AuthReviewNeededFormModal} from "../../offer-at-risk/auth-review/AuthReviewNeededFormModal";
import {FSOSupportFormModal} from "../../offer-at-risk/fso-inquiry/FSOSupportFormModal";
import { parseISO } from "date-fns";
import {ExportButton} from "common/component/ExportButton";
import {useExportData} from "../../export/useExportData";
import {ExportRequestModal} from "../../export/ExportRequestModal";

export type AuthorizationBillingTabAction =
    | { type: 'SET_EXPAND_TOGGLE'; item: AuthorizationBillItem }
    | { type: 'SET_MULTI_SELECT_TOGGLE'; item: AuthorizationBillItem }
    | { type: 'SET_MULTI_SELECT_TOGGLE_ALL'; items: AuthorizationBillItem[] }
    | { type: 'SET_SORT_BY'; sortBy: SortBy<AuthorizationBillItem> }
    | { type: 'SUBMIT_AT_RISK' }
    | { type: 'SET_AT_RISK_REASONS'; atRiskReasons: string[] }
    | { type: 'SET_FILTER'; filter: AuthorizationBillingTabFilterValue }
    | RelayPaginationActionPayload

export interface AuthorizationBillingTabState {
    pagination: RelayPaginationState;
    filter: AuthorizationBillingTabFilterValue;
    showTreatmentHold$: BehaviorSubject<boolean>;
    showStaffingPlan$: BehaviorSubject<boolean>;
    showStaffingAction$: BehaviorSubject<boolean>;
    showNoContact$: BehaviorSubject<boolean>;
    showNoAnActiveClient$: BehaviorSubject<boolean>;
    showAuthReviewNeeded$: BehaviorSubject<boolean>;
    showClientTransferring$: BehaviorSubject<boolean>;
    showAddendum$: BehaviorSubject<StaffingAddendumAction>;
    showFsoSupport$: BehaviorSubject<boolean>;
    selectedGuids: string[];
    selectedItems: AuthorizationBillItem[];
    expandedGuids: string[];
    atRiskReasons: string[]
    sortBy: SortBy<AuthorizationBillItem>;
}

export type  AuthorizationBillingTabFilterValue = {
    offeredHourStatus: string;
    procedureCode: string;
}

export function filterReasonsBySelectedItems(reasons: string[], selectedItems: AuthorizationBillItem[]): string[] {
    if (shouldDisableAuthReviewNeededOption(selectedItems)) {
        return reasons.filter(reason => reason !== AtRiskReasonValueToName.get(AtRiskReason.Auth_Review_Needed));
    }

    return reasons;
}

export function shouldDisableRiskReasonOption(checked: string[], option: string, selectedItems: AuthorizationBillItem[] = []): boolean {
    if (shouldDisableAuthReviewNeededOption(selectedItems) && option === AtRiskReasonValueToName.get(AtRiskReason.Auth_Review_Needed)) {
        return true;
    }

    if (checked.length === 0) {
        return false;
    }
    const isStaffingPlanSelected = checked.includes(AtRiskReasonValueToName.get(AtRiskReason.Staffing) ?? '');
    const isFamilyOnHoldSelected = checked.includes(AtRiskReasonValueToName.get(AtRiskReason.Treatment_Hold) ?? '')
    const isClientTransferringSelected = checked.includes(AtRiskReasonValueToName.get(AtRiskReason.Client_is_transferring) ?? '')
    const isNoContactSelected = checked.includes(AtRiskReasonValueToName.get(AtRiskReason.No_Contact) ?? '')
    const isReviewNeededSelected = checked.includes(AtRiskReasonValueToName.get(AtRiskReason.Auth_Review_Needed) ?? '')

    const isNotActiveClientSelected: boolean = checked.includes(AtRiskReasonValueToName.get(AtRiskReason.Not_an_Active_Client) ?? '')
    if (isNotActiveClientSelected) {
        return option !== AtRiskReasonValueToName.get(AtRiskReason.Not_an_Active_Client);
    }

    if (isFamilyOnHoldSelected) {
        return option !== AtRiskReasonValueToName.get(AtRiskReason.Treatment_Hold);
    }

    if (isClientTransferringSelected) {
        return option !== AtRiskReasonValueToName.get(AtRiskReason.Client_is_transferring);
    }

    if (isNoContactSelected) {
        return option !== AtRiskReasonValueToName.get(AtRiskReason.No_Contact);
    }

    if (isReviewNeededSelected) {
        return option !== AtRiskReasonValueToName.get(AtRiskReason.Auth_Review_Needed);
    }

    return isStaffingPlanSelected ? option !== AtRiskReason.Staffing : option === AtRiskReason.Staffing;
}

// BHPNPP-2693,  BHPNPP-2779
function shouldDisableAuthReviewNeededOption(selectedItems: AuthorizationBillItem[] = []): boolean {
    const hasAuthReviewNeeded = selectedItems.some(
        item => item.atRiskResponseReasons?.includes(AtRiskReasonValueToName.get(AtRiskReason.Auth_Review_Needed) ?? '')
    );
    const hasSubmittedAuthReviewedDate = selectedItems.some(item => item.submittedAuthReviewedDate);
    return hasAuthReviewNeeded || hasSubmittedAuthReviewedDate;
}


export function authorizationBillingTabReducerInitState(): AuthorizationBillingTabState {
    return {
        pagination: new RelayPaginationState('0', 8),
        filter: {
            procedureCode: "",
            offeredHourStatus: "At_Risk",
        },
        selectedGuids: [],
        selectedItems: [],
        expandedGuids: [],
        atRiskReasons: [],
        showStaffingPlan$: new BehaviorSubject<boolean>(false),
        showTreatmentHold$: new BehaviorSubject<boolean>(false),
        showStaffingAction$: new BehaviorSubject<boolean>(false),
        showClientTransferring$: new BehaviorSubject<boolean>(false),
        showNoContact$: new BehaviorSubject<boolean>(false),
        showNoAnActiveClient$: new BehaviorSubject<boolean>(false),
        showAuthReviewNeeded$: new BehaviorSubject<boolean>(false),
        showFsoSupport$: new BehaviorSubject<boolean>(false),
        showAddendum$: new BehaviorSubject<StaffingAddendumAction>({visible: false, addendumType: ''}),
        sortBy: {
            column: "startDate",
            order: SortOrder.ASC,
        }
    }
}

export function authorizationBillingTabReducer(
    state: AuthorizationBillingTabState,
    action: AuthorizationBillingTabAction
): AuthorizationBillingTabState {
    switch (action.type) {
        case "SET_SORT_BY":
            return {
                ...state,
                sortBy: action.sortBy
            };
        case "SET_AT_RISK_REASONS":
            return {
                ...state,
                atRiskReasons: action.atRiskReasons
            }
        case "SUBMIT_AT_RISK":
            return {
                ...state,
                atRiskReasons: [],
                selectedGuids: [],
                selectedItems: [],
            }
        case "SET_MULTI_SELECT_TOGGLE":
            const newItems = toggleItem(state.selectedItems, action.item);
            return {
                ...state,
                selectedGuids: newItems.map((item) => item.guid),
                selectedItems: newItems,
                atRiskReasons: filterValidRiskReasons(state.atRiskReasons, newItems)
            }
        case "SET_MULTI_SELECT_TOGGLE_ALL":
            const items = toggleItems(
                state.selectedItems,
                action.items)
            return {
                ...state,
                atRiskReasons: filterValidRiskReasons(state.atRiskReasons, items),
                selectedItems: items,
                selectedGuids: items.map((item) => item.guid)
            }
        case "SET_EXPAND_TOGGLE":
            return {
                ...state,
                expandedGuids: toggleItemGuids(state.expandedGuids, action.item.guid)
            }
        case 'RELAY_PAGINATION_ACTION':
            return {
                ...state,
                atRiskReasons: [],
                selectedGuids: [],
                selectedItems: [],
                expandedGuids: [],
                pagination: reduceRelayPaginationAction(state.pagination, action)
            };

        case 'SET_FILTER':
            return {
                ...state,
                filter: action.filter,
                pagination: new RelayPaginationState('0', 8),
                selectedGuids: [],
                selectedItems: [],
                expandedGuids: [],
                atRiskReasons: []
            }
        default:
            return state;
    }
}

const toggleItemGuids = (before: string[], toggleItem: string) => {
    if (before.includes(toggleItem)) {
        return before.filter((item) => item.localeCompare(toggleItem));
    }

    return [...before, toggleItem];
}

const toggleItem = (before: AuthorizationBillItem[], toggleItem: AuthorizationBillItem) => {
    const beforeIds = new Set(before.map((item) => item.guid));
    if (beforeIds.has(toggleItem.guid)) {
        return before.filter((item) => item.guid.localeCompare(toggleItem.guid));
    }

    return [...before, toggleItem];
}

const toggleItems = (before: AuthorizationBillItem[], items: AuthorizationBillItem[]) => {
    if (before.length !== 0) {
        return []
    }

    return [...items]
}

const filterValidRiskReasons = (reasons: string[], selectedItems: AuthorizationBillItem[]): string[] => {
    if (reasons.length === 0) {
        return reasons;
    }

    // Filter out reasons that would be disabled based on current selection
    return reasons.filter(reason => !shouldDisableRiskReasonOption(reasons, reason, selectedItems));
}

export const AUTHORIZATION_ITEM_LIST = gql`
query authorizationItemsQuery($first: Int = 20, $after: String, $params: AuthorizationItemsQueryParamsInput!, $authorizationGuid: UUID,
$authorizationGuidStr: String, $requestTypes: [RequestType]) {
    openCaseSubmitRequestQuery(
        authorizationGuid: $authorizationGuidStr
        requestTypes: $requestTypes
    ) {
        guid, authorizationGuid, caseNumber, clientGuid,
        reason, recordType, requestType, status
    }
    authorizationItemsQuery(
        first: $first
        after: $after
        params: $params
        authorizationGuid: $authorizationGuid
    ) {
        edges {
            cursor
            node {
                guid
                status
                startDate
                endDate
                closedReason
                procedureCode
                modifier
                practitionerRole
                tier
                minimumHoursAuthorized
                maximumHoursAuthorized
                hoursBilled
                directCareHoursRequired
                dateAtRiskPublished
                atRiskResponseDueDate
                staffingAddendumDueDate
                staffingAddendumSubmittedDate
                atRiskResponseDate
                atRiskResponseReasons
                offeredHoursStatus
                lastModifiedDate
                lastModifiedBy
                submittedAuthReviewedDate
                
                chargeEntries {
                    guid
                    name
                    serviceDate
                    procedureCode
                    hoursBilled
                    practitionerName
                    submissionDate
                }
                
                staffingPlan {
                    comments
                    fullOfferStartDate
                    reviewedAndApprovedByClientFamilyDate
                    offeredParaprofessionalHours
                    offeredMidLevelHours
                    offeredHighLevelHours
                    offeredGroupCareHours
                }
                
                submitter {
                    firstName
                    lastName
                }
            }
        }
        totalElements
        pageInfo {
            hasPreviousPage
            endCursor
            hasNextPage
            startCursor
        }
    }

}
`

export const EXPORT_AUTHORIZATION_BILLING = gql`
    mutation ($filters: String) {
        exportAuthorizationBilling(filters: $filters) {
            guid
            status
        }
    }
`;

type AuthorizationItemsQueryResponse = RelayConnection<AuthorizationBillItem> & { totalElements: number };


type AuthorizationBillingTabProps = {
    authorization: AuthorizationInfo;
    currentState: AuthorizationBillingTabState;
    dispatch: Dispatch<AuthorizationBillingTabAction>;
    clientGuid: string;
    onRiskReasonsSubmitted?: Function;
}

export const AuthorizationBillingTab = ({
                                            authorization,
                                            dispatch,
                                            currentState,
                                            clientGuid,
                                            onRiskReasonsSubmitted = _.noop
                                        }: AuthorizationBillingTabProps) => {
    const authorizationGuid = authorization.guid;
    const {
        filter,
        pagination,
        sortBy,
        expandedGuids,
        selectedGuids,
        selectedItems,
        atRiskReasons,
        showStaffingPlan$,
        showTreatmentHold$,
        showStaffingAction$,
        showNoContact$,
        showAddendum$,
        showNoAnActiveClient$,
        showClientTransferring$,
        showAuthReviewNeeded$,
        showFsoSupport$,
    } = currentState;
    const {execute, data, isRunning$, error} = useLazyQuery$<{
        authorizationGuid: string;
        authorizationGuidStr: string;
        first: number;
        after: string;
        requestTypes: string[];
        params: {
            procedureCode: string | null,
            offeredHoursStatus: string | null,
            sortDir: SortOrder,
            sortBy: string,
        }
    }, { authorizationItemsQuery: AuthorizationItemsQueryResponse, openCaseSubmitRequestQuery: SubmittedRequestInfo[] }>(
        AUTHORIZATION_ITEM_LIST,
        {
            ...pagination.toGraphQlParams(),
            authorizationGuid: authorizationGuid,
            authorizationGuidStr: authorizationGuid,
            requestTypes: ["Treatment_Hold", "No_Contact", "Client_Return_Transfer_Request"],
            params: {
                procedureCode: filter.procedureCode !== "" ? filter.procedureCode : null,
                offeredHoursStatus: filter.offeredHourStatus !== "" ? filter.offeredHourStatus : null,
                sortDir: sortBy.order,
                sortBy: sortBy.column,
            }
        }
    )

    const startFetch = () => {
        execute({
            ...pagination.toGraphQlParams(),
            authorizationGuid: authorizationGuid,
            authorizationGuidStr: authorizationGuid,
            requestTypes: ["Treatment_Hold", "No_Contact", "Client_Return_Transfer_Request"],
            params: {
                procedureCode: filter.procedureCode !== "" ? filter.procedureCode : null,
                offeredHoursStatus: filter.offeredHourStatus !== "" ? filter.offeredHourStatus : null,
                sortDir: sortBy.order,
                sortBy: sortBy.column,
            }
        }).then(() => console.debug("Initial fetch"))
    }

    const {submit: submitAtRiskReasons, isSubmitting$} = useSubmitAtRiskReasons(async () => {
        await onRiskReasonsSubmitted();
        startFetch() // re-fetch
    });

    const submit = async () => {
        if (atRiskReasons.includes(AtRiskReasonValueToName.get(AtRiskReason.Staffing) ?? "")) {
            showStaffingAction$.next(true);
        } else if (atRiskReasons.includes(AtRiskReasonValueToName.get(AtRiskReason.Treatment_Hold) ?? "")) {
            // Check if there's already an open Treatment Hold case
            const hasOpenTreatmentHold = data?.openCaseSubmitRequestQuery?.some(
                (c) => c.requestType === 'Treatment_Hold'
            );
            if (hasOpenTreatmentHold) {
                // Skip modal and submit directly (same-type case already exists)
                await submitAtRiskReasons({
                    authItemGuids: selectedGuids,
                    reasons: atRiskReasons,
                    staffingPlan: null,
                    staffingAction: null,
                    addendumReportGuid: null,
                    treatmentHold: null,
                    notAnActiveClient: null,
                    clientTransferring: null,
                    noContact: null,
                    authReviewNeeded: null,
                    updateAuthItemOnly: true,
                });
                dispatch({type: "SUBMIT_AT_RISK"});
            } else {
                showTreatmentHold$.next(true);
            }
        } else if (atRiskReasons.includes(AtRiskReasonValueToName.get(AtRiskReason.Not_an_Active_Client) ?? "")) {
            // BHPNPP-2023
            const selectedAuthItems = authItems.filter((item) => selectedGuids.includes(item.guid));
            if (isSelectingBilledMonths(selectedAuthItems, authorization)) {
                showToast.error(getErrMsgForSelectingActiveClient(authorization), {duration: 10});
                return;
            }

            showNoAnActiveClient$.next(true);
        } else if (atRiskReasons.includes(AtRiskReasonValueToName.get(AtRiskReason.Client_is_transferring) ?? "")) {
            showClientTransferring$.next(true);
        } else if (atRiskReasons.includes(AtRiskReasonValueToName.get(AtRiskReason.No_Contact) ?? "")) {
            // Check if there's already an open No Contact case
            const hasOpenNoContact = data?.openCaseSubmitRequestQuery?.some(
                (c) => c.requestType === 'No_Contact' 
            );
            if (hasOpenNoContact) {
                // Skip modal and submit directly (same-type case already exists)
                await submitAtRiskReasons({
                    authItemGuids: selectedGuids,
                    reasons: atRiskReasons,
                    staffingPlan: null,
                    staffingAction: null,
                    addendumReportGuid: null,
                    treatmentHold: null,
                    notAnActiveClient: null,
                    clientTransferring: null,
                    noContact: null,
                    authReviewNeeded: null,
                    updateAuthItemOnly: true,
                });
                dispatch({type: "SUBMIT_AT_RISK"});
            } else {
                showNoContact$.next(true);
            }
        } else if (atRiskReasons.includes(AtRiskReasonValueToName.get(AtRiskReason.Auth_Review_Needed) ?? "")) {
            showAuthReviewNeeded$.next(true);
        } else {
            if (atRiskReasons.includes(AtRiskReasonValueToName.get(AtRiskReason.Switched_to_Caregiver_Mediated_Flex_Tx) ?? "")) {
                const invalidItems = authItems.filter((item) => selectedGuids.includes(item.guid) && "H2014" === item.procedureCode);
                if (invalidItems.length > 0) {
                    showToast.error("Switched to Caregiver Mediated - Flex Tx is not a valid response for Group Procedure Code. Please revise", {duration: 20});
                    return;
                }
            }

            if (atRiskReasons.includes(AtRiskReasonValueToName.get(AtRiskReason.Full_offer_Billing_Pending) ?? "")) {
                let ninetyDaysAgo = new Date();
                ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
                const invalidItems = authItems.filter((item) => selectedGuids.includes(item.guid) && parseISO(item.endDate).getTime() < ninetyDaysAgo.getTime());
                if (invalidItems.length > 0) {
                    showToast.error("The response 'Full offer - Billing Pending' is not valid for service dates where the approved billing has not been processed within 90 days of the service date. Please revise.", {duration: 20});
                    return;
                }
            }

            await submitAtRiskReasons({
                authItemGuids: selectedGuids,
                reasons: atRiskReasons,
                staffingPlan: null,
                staffingAction: null,
                addendumReportGuid: null,
                treatmentHold: null,
                notAnActiveClient: null,
                clientTransferring: null,
                noContact: null,
                authReviewNeeded: null,
                updateAuthItemOnly: false,
            });

            dispatch({type: "SUBMIT_AT_RISK"})
        }
    }

    const {isRunning$: isExporting$, execute: exportData} = useExportData(EXPORT_AUTHORIZATION_BILLING);
    const isExporting = useObservable(isExporting$, false);
    const [exportGuid, setExportGuid] = useState<string | null>(null);
    const handleExport = async () => {
        const guid = await exportData(JSON.stringify({
            authorizationGuid,
            procedureCode: filter.procedureCode !== "" ? filter.procedureCode : null,
            offeredHoursStatus: filter.offeredHourStatus !== "" ? filter.offeredHourStatus : null,
            sortDir: sortBy.order,
            sortBy: sortBy.column,
        }));
        if (guid) {
            setExportGuid(guid);
        }
    };

    const isSubmitting = useObservable(isSubmitting$, false);

    useEffect(() => {
        startFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loading = useObservable(isRunning$, true);
    const totalAuthorizationItems = data?.authorizationItemsQuery?.totalElements ?? 0;
    const {onPaginationClick} = useRelayPagination(dispatch, totalAuthorizationItems);

    const {
        staffingAction,
        onStaffingPlanActionSelected,
        addendumStaffingAction
    } = useStaffingAction(showStaffingPlan$, showAddendum$);
    const submitStaffing = useCallback(async (staffingAction: StaffingAction | null, staffingPlan: StaffingFormInput | null, addendumReportGuid: string | null) => {
        await submitAtRiskReasons({
            authItemGuids: selectedGuids,
            reasons: atRiskReasons,
            treatmentHold: null,
            notAnActiveClient: null,
            clientTransferring: null,
            noContact: null,
            authReviewNeeded: null,
            updateAuthItemOnly: false,
            staffingAction,
            staffingPlan,
            addendumReportGuid,
        });
        dispatch({type: "SUBMIT_AT_RISK"});
    }, [selectedGuids, atRiskReasons, submitAtRiskReasons, dispatch]);
    const onStaffingActionFinished = useCallback(async (action: StaffingAction) => {
        const hasNextStep = onStaffingPlanActionSelected(action);
        if (!hasNextStep) {
            await submitStaffing(action, null, null);
        }
    }, [submitStaffing, onStaffingPlanActionSelected]);
    const onStaffingAddendumFinished: StaffingAddendumFinisher = useCallback(async (newReportGuid: string | null) => {
        await submitStaffing(staffingAction, null, newReportGuid);
    }, [staffingAction, submitStaffing]);

    if (error) {
        return <ErrorDisplay error={error} title="Error loading authorization items" />;
    }

    const authItems = data?.authorizationItemsQuery?.edges.map((edge) => edge.node) || [];
    const sortedItems = authItems.map(i => {
        let entries = i.chargeEntries || [];
        entries = entries
            .slice()
            .sort((a, b) => (-1) * a.serviceDate.localeCompare(b.serviceDate));
        return {...i, chargeEntries: entries};
    })

    // Filter selected items to only include At Risk items
    const selectedAtRiskItems = sortedItems.filter(item => 
        selectedGuids.includes(item.guid) && 
        OfferedHoursStatusMap.get(item.offeredHoursStatus) === OfferedHoursStatus.At_Risk
    );
    const selectedAtRiskGuids = selectedAtRiskItems.map(item => item.guid);

    return <div>
        <Pane display='flex' justifyContent='flex-end' padding={2} alignItems='center'>
            <div
                onClick={() => {
                    if (!isSubmitting && selectedAtRiskItems.length === 0) {
                        showToast.warning('Please select one or more items in the list below');
                    }
                }}
            >
                <FatOutlineButton color={'atlantis'}
                                text={isSubmitting ? 'Submitting...' : 'Submit FSO Inquiry'}
                                disabled={isSubmitting || selectedAtRiskItems.length === 0}
                                onClick={() => showFsoSupport$.next(true)}/>
            </div>
            <AuthorizationBillingTabFilter initFilterValue={filter} dispatch={dispatch}/>
            <div className="mr-3 flex items-center">
                <ExportButton
                    onClick={handleExport}
                    disabled={isExporting}
                    minWidth="7rem"
                />
                <ExportRequestModal guid={exportGuid} onClose={() => setExportGuid(null)}/>
            </div>
            <RelayPagination pageInfo={data?.authorizationItemsQuery?.pageInfo}
                             count={totalAuthorizationItems}
                             onClick={onPaginationClick}/>
        </Pane>
        <AuthorizationBillingTable expandedGuids={expandedGuids}
                                   selectedGuids={selectedGuids}
                                   dispatch={dispatch}
                                   data={sortedItems}
                                   loading={loading}
                                   sortBy={sortBy}/>

        {
            selectedGuids.length > 0 &&
            <div className='absolute flex' style={{width: 200, bottom: 20, right: 20}}>
                <FatOutlineButton color={'green'}
                                  text={isSubmitting ? 'Loading...' : 'Submit'}
                                  disabled={atRiskReasons.length === 0 || isSubmitting}
                                  onClick={submit}/>
            </div>
        }

        <NoContactFormModal
            visible$={showNoContact$}
            authorizationGuid={authorizationGuid}
            skipValidation={true}
            onFinished={
                async (values) => {
                    await submitAtRiskReasons({
                        authItemGuids: selectedGuids,
                        reasons: atRiskReasons,
                        staffingPlan: null,
                        staffingAction: null,
                        addendumReportGuid: null,
                        treatmentHold: null,
                        notAnActiveClient: null,
                        clientTransferring: null,
                        authReviewNeeded: null,
                        updateAuthItemOnly: false,
                        noContact: values,
                    })

                    dispatch({type: "SUBMIT_AT_RISK"});
                }}/>

        <ConsultWithCCMNotAnActiveClientFormModal
            visible$={showNoAnActiveClient$}
            onFinished={
                async (values) => {
                    await submitAtRiskReasons({
                        authItemGuids: selectedGuids,
                        reasons: atRiskReasons,
                        staffingPlan: null,
                        staffingAction: null,
                        addendumReportGuid: null,
                        treatmentHold: null,
                        clientTransferring: null,
                        notAnActiveClient: {
                            description: buildNotAnActiveClientDescription(values, selectedItems),
                            lastServiceDate: values?.lastServiceDate ?? null
                        },
                        noContact: null,
                        updateAuthItemOnly: false,
                        authReviewNeeded: null,
                    })

                    dispatch({type: "SUBMIT_AT_RISK"});
                }}/>

        <AuthReviewNeededFormModal
            visible$={showAuthReviewNeeded$} onFinished={
            async (values) => {
                await submitAtRiskReasons({
                    authItemGuids: selectedGuids,
                    reasons: atRiskReasons,
                    staffingPlan: null,
                    staffingAction: null,
                    addendumReportGuid: null,
                    treatmentHold: null,
                    clientTransferring: null,
                    notAnActiveClient: null,
                    noContact: null,
                    updateAuthItemOnly: false,
                    authReviewNeeded: values,
                });

                dispatch({type: "SUBMIT_AT_RISK"});
            }
        }
        />
        <TreatmentHoldFormModal
            visible$={showTreatmentHold$}
            authorizationGuid={authorizationGuid}
            authorization={authorization}
            skipValidation={true}
            onRedirectToStaffingPlan={() => showStaffingPlan$.next(true)}
            onFinished={async (values) => {
                await submitAtRiskReasons({
                    authItemGuids: selectedGuids,
                    reasons: atRiskReasons,
                    staffingPlan: null,
                    staffingAction: null,
                    addendumReportGuid: null,
                    treatmentHold: values,
                    clientTransferring: null,
                    notAnActiveClient: null,
                    noContact: null,
                    updateAuthItemOnly: false,
                    authReviewNeeded: null,
                });

                dispatch({type: "SUBMIT_AT_RISK"});
            }}
        />

        <ClientTransferringFormModal authorization={authorization} visible$={showClientTransferring$}
                                     onFinished={async (value) => {
                                         await submitAtRiskReasons({
                                             authItemGuids: selectedGuids,
                                             reasons: atRiskReasons,
                                             staffingPlan: null,
                                             staffingAction: null,
                                             addendumReportGuid: null,
                                             treatmentHold: null,
                                             clientTransferring: value,
                                             notAnActiveClient: null,
                                             noContact: null,
                                             updateAuthItemOnly: false,
                                             authReviewNeeded: null,
                                         });

                                         dispatch({type: "SUBMIT_AT_RISK"});
                                     }}/>

        <StaffingPlanActionModal visible$={showStaffingAction$} onFinished={onStaffingActionFinished}/>

        <StaffingPlanFormModal
            visible$={showStaffingPlan$}
            authorization={authorization}
            onFinished={async (values) => {
                await submitStaffing(staffingAction, values, null);
            }}
        />

        <ClientReportModal
            showModal={addendumStaffingAction.visible}
            hideModalFunc={() => showAddendum$.next({addendumType: '' as StaffingAddendumType, visible: false})}
            clientGuid={clientGuid}
            defaultAuth={authorization}
            staffingAddendumReport={{
                type: addendumStaffingAction.addendumType as StaffingAddendumType,
                onFinished: onStaffingAddendumFinished
            }}
            authBillingTab
        />

        <FSOSupportFormModal
            visible$={showFsoSupport$}
            authItemGuids={selectedAtRiskGuids}
        />
    </div>
}

const useSubmitAtRiskReasons = (successCallback: () => void | Promise<void>) => {
    const {submit, isRunning$} = useSubmitAtRiskReasonsMutation$(successCallback);
    return {
        isSubmitting$: isRunning$,
        submit: submit,
    }
}
